import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal, TextInput, Theme } from '../../../components';
import { FoodType } from '../../../models';
import { foodTypeActions, foodTypeSelectors, userActions, userSelectors } from '../../../redux';
import { getFoodTypeName } from '../../../redux/foodtypes/selectors';

import './AddEditStore.scss';
import './EditWarningTimerModal.scss';

interface Props {
  closeModal: () => void;
  isOpen: boolean;
}

const EditWarningTimerModal: FC<Props> = ({ closeModal, isOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector(userSelectors.getUserInfo);
  const isEditLoading = useSelector(userSelectors.isWarningTimeLoading);
  const warningTimerSetting = String(useSelector(userSelectors.getWarningTime));
  const foodTypeWithMinHoldingTime = useSelector(foodTypeSelectors.getFoodTypeWithMinHoldingTime);

  const [warningTimer, setWarningTimer] = useState<string>();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  /**
   * Returns the food type that has a holding time lower than the entered holding time
   */
  const getErrorFoodType = useCallback((): FoodType | null => {
    if (!warningTimer) {
      return null;
    }
    try {
      const newWarningTime: number = parseFloat(warningTimer || warningTimerSetting);
      if (newWarningTime > parseFloat(foodTypeWithMinHoldingTime.maxHoldTime)) {
        return foodTypeWithMinHoldingTime;
      }
    } catch (err) {
      // ignore parsing errors
    }
    return null;
  }, [warningTimer, warningTimerSetting]);

  useEffect(() => {
    if (isSaveClicked && !isEditLoading) {
      dispatch(userActions.getUserInfo());
      handleCloseModal();
    }
  }, [isSaveClicked, isEditLoading]);

  useEffect(() => {
    const errorFoodType = getErrorFoodType();
    let newError = '';
    if (errorFoodType) {
      newError = t('errorWarningTimeLowerThanFoodType', {
        name: getFoodTypeName(errorFoodType),
        holdingTime: errorFoodType.maxHoldTime,
      });
    }
    setError(newError);
  }, [setError, getErrorFoodType]);

  useEffect(() => {
    setIsValid(warningTimer && warningTimerSetting !== warningTimer && !error);
  }, [warningTimer, error]);

  useEffect(() => {
    if (warningTimerSetting) {
      setWarningTimer(warningTimerSetting);
    }
  }, [warningTimerSetting]);

  useEffect(() => {
    if (warningTimerSetting) {
      setWarningTimer(warningTimerSetting);
    }
    dispatch(foodTypeActions.getFoodTypeWithMinHoldingTime());
  }, [isOpen]);

  useEffect(() => {
    dispatch(foodTypeActions.getFoodTypeWithMinHoldingTime());
  }, []);

  const handleCloseModal = () => {
    setWarningTimer('');
    closeModal();
  };

  const save = () => {
    setIsSaveClicked(true);
    //TODO handle parse errors
    dispatch(userActions.updateWarningTime(parseFloat(warningTimer || warningTimerSetting), user.country.id));
  };

  return (
    <Modal className='warning-timer-modal' title={t('lblChangeWarning')} isOpen={isOpen} closeModal={() => closeModal()}>
      <div className='add-edit-container'>
        <p className='description'>{t('lblChangeWarningTimesSubTitle')}</p>
        {error && <p className='warning'>{error}</p>}

        <TextInput
          type='timer'
          min='0'
          label={t('lblWarningTime')}
          placeholder={t('lblWarningTime')}
          onChange={setWarningTimer}
          value={warningTimer}
        />

        <div className='buttons'>
          <Button theme={Theme.INVERSE} onClickCallback={handleCloseModal}>
            {t('lblCancel')}
          </Button>
          <Button theme={Theme.SECONDARY} onClickCallback={save} isLoading={isEditLoading} isDisabled={!isValid}>
            {t('lblSave')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditWarningTimerModal;
